import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import {
  Button,
  ButtonDirective,
  ButtonIcon,
  ButtonLabel,
} from 'primeng/button'
import { MultiSelect } from 'primeng/multiselect'
import { Dialog } from 'primeng/dialog'
import { DatePicker } from 'primeng/datepicker'
import { InputSwitch } from 'primeng/inputswitch'
import { Select } from 'primeng/select'
import { Textarea } from 'primeng/textarea'
import { InputText } from 'primeng/inputtext'
import { WindowMaximizeIcon } from 'primeng/icons'
import { UserService } from '../../../users/services/user.service'
import { ToastService } from '../../../../shared/services/toast.service'
import { UserModel } from '../../../../core/models/user.model'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { EventsService } from '../../services/events.service'
import { Checkbox } from 'primeng/checkbox'
import { DropdownModule } from 'primeng/dropdown'
import { InputNumber } from 'primeng/inputnumber'
import { RadioButton } from 'primeng/radiobutton'
import { ToggleButton } from 'primeng/togglebutton'
import { TabView } from 'primeng/tabview'
import { TabPanel } from 'primeng/tabview'

@Component({
  selector: 'app-new-modal',
  standalone: true,
  imports: [
    MultiSelect,
    Dialog,
    DatePicker,
    Select,
    Textarea,
    InputText,
    ButtonDirective,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    DropdownModule,
    InputNumber,
    NgForOf,
    Checkbox,
    ToggleButton,
    RadioButton,
    TabView,
    TabPanel,
  ],
  templateUrl: './new-modal.component.html',
  styleUrl: './new-modal.component.scss',
})
export class NewModalComponent implements OnInit {
  @Input() isVisible: boolean = false
  @Output() isVisibleChange = new EventEmitter<boolean>()
  public allTechnicians: UserModel[] = []
  public allEventTypes: any = []
  public erpSource: string = ''
  public form: FormGroup
  private userModifiedName: boolean = false
  public submitting: boolean = false

  @Input() startTimeInput: Date
  @Input() durationInput: Date

  public startTime: Date
  public duration: Date

  @Output() evenementCree = new EventEmitter<void>()

  constructor(
    private userServices: UserService,
    private toastService: ToastService,
    private eventsService: EventsService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.loadAllTechnicians()
    this.initForm()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      this.open()
    }
  }

  hide() {
    this.isVisible = false
    this.isVisibleChange.emit(false)
  }

  open() {
    if (this.getSelectedTechniciansFromStorage().length === 0) return

    this.startTime = this.startTimeInput
    this.duration = this.durationInput

    this.erpSource = this.getErpSource()
    this.userModifiedName = false // Reset the flag when opening the modal

    if (this.erpSource !== '') {
      this.loadAllEventTypes(this.erpSource)

      this.form.patchValue({
        startTime: this.startTime ?? null,
        duration: this.duration ?? null,
        users: this.getSelectedTechniciansFromStorage(),
      })
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      users: [[], Validators.required],
      eventType: [null, Validators.required],
      name: ['', Validators.required],
      description: [''],
      startTime: [null, Validators.required],
      duration: [null, Validators.required],
      address: [''],
      postalCode: [''],
      city: [''],
      recurrenceEnabled: [false],
      recurrenceInterval: [1],
      recurrenceUnit: ['day'],
      recurrenceEndType: ['never'], // Assurer que la valeur par défaut est correcte
      recurrenceEndAfter: [null],
      recurrenceEndDate: [null],
      eprSource: [this.erpSource],
    })

    if (this.form) {
      const eventTypeControl = this.form.get('eventType')
      if (eventTypeControl) {
        eventTypeControl.valueChanges.subscribe((selectedEventType) => {
          // Only update the name if it's empty or hasn't been modified
          const nomControl = this.form.get('nom')
          if (
            selectedEventType &&
            nomControl &&
            (!nomControl.value || !this.userModifiedName)
          ) {
            nomControl?.setValue(selectedEventType.name)
          }
        })
      }
      // Track when user manually changes the name field
      const nomControl = this.form.get('nom')
      if (nomControl) {
        nomControl.valueChanges.subscribe((value) => {
          if (value && value.trim() !== '') {
            this.userModifiedName = true
          }
        })
      }
    }
  }

  loadAllTechnicians(): void {
    this.userServices.getUsersWithFilters({}).subscribe({
      next: (technicians) => {
        this.allTechnicians = technicians.map((tech) => ({
          ...tech,
          fullName: `${tech.firstname} ${tech.lastname}`,
        }))
      },
      error: (error) => {
        this.toastService.showError(error.message)
      },
    })
  }

  getSelectedTechniciansFromStorage(): UserModel[] {
    const stored = localStorage.getItem('calendar_selected_users')

    if (!stored) return []
    try {
      const parsed: UserModel[] = JSON.parse(stored)
      return this.allTechnicians.filter((tech) =>
        parsed.some((p) => p.id === tech.id)
      )
    } catch (e) {
      console.error('Erreur parsing localStorage:', e)
      return []
    }
  }

  getErpSource(): string {
    const stored = localStorage.getItem('calendar_selected_users')

    if (!stored) return ''

    try {
      const parsed: UserModel[] = JSON.parse(stored)
      const firstSource = parsed[0]?.erpSource

      const allSame = parsed.every((tech) => tech.erpSource === firstSource)
      return allSame ? firstSource : ''
    } catch (e) {
      console.error('Erreur parsing localStorage:', e)
      return ''
    }
  }

  loadAllEventTypes(erpSource: string): void {
    this.eventsService.getEventsTypes(erpSource).subscribe({
      next: (data) => {
        this.allEventTypes = data
      },
      error: (error) => {
        this.toastService.showError(error.message)
      },
    })
  }

  createEvenement() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      this.toastService.showError(
        "Formulaire de création d'événement incomplet"
      )
      return
    }

    // Activer l'indicateur de chargement
    this.submitting = true

    const value = this.form.value

    const payload = {
      eventTypeId: value.eventType?.id,
      name: value.name,
      description: value.description || '',
      startDate: this.formatDateToISO(value.startTime),
      duration: this.formatDuration(value.duration),
      address: value.address || '',
      postalCode: value.postalCode || '',
      locality: value.city || '',
      isAllDay: false,
      usersIds: value.users.map((u: UserModel) => u.id),
      recurrence: {
        frequency: value.recurrenceUnit || '',
        interval: value.recurrenceInterval || 1,
        daysOfWeek: this.selectedDays || [],
        count: value.recurrenceEndAfter || null,
        until: value.recurrenceEndDate || null,
      },
    }

    if (value.recurrenceEnabled) {
      this.eventsService
        .storeWithRecurrence({
          ...payload,
          erpSource: this.getErpSource(),
        })
        .subscribe({
          next: (data) => {
            this.evenementCree.emit()
            this.toastService.showSuccess(
              'Nouvel évenement avec récurrence ajouté ✨'
            )
            this.resetForm()
            this.submitting = false
            this.hide()
          },
          error: (error) => {
            this.submitting = false
            this.toastService.showError(error.message)
          },
        })
    } else {
      this.eventsService.store(payload).subscribe({
        next: (data) => {
          this.evenementCree.emit()
          this.toastService.showSuccess('Nouvel évenement ajouté ✨')
          this.resetForm()
          this.submitting = false
          this.hide()
        },
        error: (error) => {
          this.submitting = false
          this.toastService.showError(error.message)
        },
      })
    }
  }

  formatDuration(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  resetForm(): void {
    // Réinitialiser le formulaire
    this.form.reset({
      users: [],
      eventType: null,
      name: '',
      description: '',
      startTime: null,
      duration: null,
      address: '',
      postalCode: '',
      locality: '',
      recurrenceEnabled: false,
      recurrenceInterval: 1,
      recurrenceUnit: 'day',
      recurrenceEndType: 'never',
      recurrenceEndAfter: null,
      recurrenceEndDate: null,
    })

    // Réinitialiser les jours sélectionnés
    this.selectedDays = []
    this.weekDays.forEach((day) => {
      day.selected = false
    })

    // Réinitialiser le flag de modification du nom
    this.userModifiedName = false
  }

  formatDateToISO(date: Date): string {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  setEventTypeName($event: any) {
    const name = this.form.get('name')
    if (name) {
      name.setValue($event.value?.name || '')
    }
  }

  recurrenceUnits = [
    { label: 'jour', value: 'day' },
    { label: 'semaine', value: 'week' },
    { label: 'mois', value: 'month' },
    { label: 'année', value: 'year' },
  ]

  weekDays = [
    { label: 'Lun', value: 1, selected: false },
    { label: 'Mar', value: 2, selected: false },
    { label: 'Mer', value: 3, selected: false },
    { label: 'Jeu', value: 4, selected: false },
    { label: 'Ven', value: 5, selected: false },
    { label: 'Sam', value: 6, selected: false },
    { label: 'Dim', value: 0, selected: false },
  ]

  selectedDays: number[] = []

  toggleDay(day: number) {
    const dayObj = this.weekDays.find((d) => d.value === day)
    if (dayObj) {
      if (dayObj.selected) {
        this.selectedDays = this.selectedDays.filter((d) => d !== day)
      } else {
        this.selectedDays.push(day)
      }
    }
  }

  isDaySelected(day: number): boolean {
    return this.selectedDays.includes(day)
  }
}
